import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export type ConceptStatus = 'active' | 'inactive';

export interface ConceptServiceRef {
  id: string;
  code: string;
  name: string;
  prefix: string;
}

export interface Concept {
  id: string;
  prefix: string;
  code: string;
  name: string;
  /** Texto para columna Servicio (nombres unidos). */
  service: string;
  serviceIds: string[];
  services: ConceptServiceRef[];
  status: ConceptStatus;
}

export interface ConceptPageResponse {
  items: Concept[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Conceptos por país (N:M con services).
 * Endpoint: `/settings/countries/{countryId}/concepts`
 */
@Injectable({ providedIn: 'root' })
export class ConceptService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private resource(countryId: string, id?: string | number): string {
    const baseUrl = `${this.apiUrl}/${countryId}/concepts`;
    return id !== undefined && id !== null ? `${baseUrl}/${id}` : baseUrl;
  }

  private normalizeStatus(status: unknown): ConceptStatus {
    return String(status ?? 'active').toLowerCase() === 'inactive'
      ? 'inactive'
      : 'active';
  }

  private mapItem(it: any): Concept {
    const services: ConceptServiceRef[] = Array.isArray(it?.services)
      ? it.services.map((s: any) => ({
          id: String(s?.id ?? ''),
          code: String(s?.code ?? ''),
          name: String(s?.name ?? ''),
          prefix: String(s?.prefix ?? ''),
        }))
      : [];
    return {
      id: String(it?.id ?? ''),
      prefix: String(it?.prefix ?? ''),
      code: String(it?.code ?? it?.prefix ?? ''),
      name: String(it?.name ?? ''),
      service: services.map((s) => s.name).filter(Boolean).join(', '),
      serviceIds: services.map((s) => s.id).filter(Boolean),
      services,
      status: this.normalizeStatus(it?.status),
    };
  }

  private toApiPayload(payload: {
    prefix?: string;
    code?: string;
    name?: string;
    status?: string;
    serviceIds?: string[];
  }) {
    const status = payload.status
      ? String(payload.status).toUpperCase()
      : undefined;
    return {
      prefix: payload.prefix?.trim() || undefined,
      code: payload.code?.trim() || undefined,
      name: payload.name?.trim() || undefined,
      status,
      serviceIds: payload.serviceIds,
    };
  }

  getConcepts(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<ConceptPageResponse> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))
      .set('sort', sort)
      .set('direction', direction);
    if (search?.trim()) params = params.set('search', search.trim());
    if (status?.trim()) {
      params = params.set('status', status.trim().toUpperCase());
    }
    return this.httpClient
      .get<ApiResponse<PageableData>>(this.resource(countryId), { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((it: any) => this.mapItem(it)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          };
        }),
      );
  }

  getConceptById(countryId: string, id: string | number): Observable<Concept> {
    return this.httpClient
      .get<ApiResponse<any>>(this.resource(countryId, id))
      .pipe(map((resp) => this.mapItem(resp.data)));
  }

  createConcept(
    countryId: string,
    payload: {
      prefix?: string;
      code?: string;
      name: string;
      status?: string;
      serviceIds: string[];
    },
  ): Observable<{ message: string }> {
    return this.httpClient
      .post<{ success?: boolean; message?: string }>(
        this.resource(countryId),
        this.toApiPayload(payload),
      )
      .pipe(map((res) => ({ message: res.message || 'Concepto creado' })));
  }

  updateConcept(
    countryId: string,
    id: string | number,
    payload: {
      prefix?: string;
      code?: string;
      name?: string;
      status?: string;
      serviceIds?: string[];
    },
  ): Observable<{ message: string }> {
    return this.httpClient
      .put<{ success?: boolean; message?: string }>(
        this.resource(countryId, id),
        this.toApiPayload(payload),
      )
      .pipe(map((res) => ({ message: res.message || 'Concepto actualizado' })));
  }

  toggleConceptStatus(
    countryId: string,
    id: string | number,
  ): Observable<{ message: string }> {
    return this.httpClient
      .patch<{ success?: boolean; message?: string }>(
        `${this.resource(countryId, id)}/status`,
        {},
      )
      .pipe(
        map((res) => ({ message: res.message || 'Estatus actualizado' })),
      );
  }

  deleteConcept(
    countryId: string,
    id: string | number,
  ): Observable<{ message: string }> {
    return this.httpClient
      .delete<{ success?: boolean; message?: string }>(
        this.resource(countryId, id),
      )
      .pipe(map((res) => ({ message: res.message || 'Concepto eliminado' })));
  }
}
